import React from 'react';
import * as Inovx from '@shared';
import SystemActions from '@shared/actions/SystemActions';
import { displayApiError } from '@shared/api/apiClient';
import dataSourcesApi from '../api/dataSourcesApi';
import { DATA_SOURCE_ACTION_KEYS } from '../constants';
import { ConnectionTypeModel, DataSourceModel, DataSourceSaveModel } from '../types';
import DataSourceGrid from './DataSourceGrid';
import DataSourceWizard from './DataSourceWizard';

interface DataSourcesPageProps {
    facility: IFacilityDetail;
}

const sortSources = (sources: DataSourceModel[]) => [...sources].sort((left, right) => left.Name.localeCompare(right.Name));
const InovxControls = Inovx.Shared.Controls;

const DataSourcesPage: React.FC<DataSourcesPageProps> = ({ facility }) => {
    const [connectionTypes, setConnectionTypes] = React.useState<ConnectionTypeModel[]>([]);
    const [sources, setSources] = React.useState<DataSourceModel[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [wizardSource, setWizardSource] = React.useState<DataSourceModel | null | undefined>(undefined);
    const [testingSourceID, setTestingSourceID] = React.useState<string | null>(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        SystemActions.PublicActions.beginAction(DATA_SOURCE_ACTION_KEYS.load);
        try {
            const [loadedTypes, loadedSources] = await Promise.all([
                dataSourcesApi.getConnectionTypes(),
                dataSourcesApi.getSources(facility.ID),
            ]);
            setConnectionTypes(loadedTypes);
            setSources(sortSources(loadedSources));
        } catch (error) {
            displayApiError(error);
        } finally {
            setLoading(false);
            SystemActions.PublicActions.endAction(DATA_SOURCE_ACTION_KEYS.load);
        }
    }, [facility.ID]);

    React.useEffect(() => {
        load();
    }, [load]);

    const saveSource = async (model: DataSourceSaveModel) => {
        setSaving(true);
        SystemActions.PublicActions.beginAction(DATA_SOURCE_ACTION_KEYS.save);
        try {
            if (model.ID) {
                await dataSourcesApi.updateSource(model);
            } else {
                await dataSourcesApi.createSource(model);
            }
            setWizardSource(undefined);
            await load();
        } catch (error) {
            displayApiError(error);
        } finally {
            setSaving(false);
            SystemActions.PublicActions.endAction(DATA_SOURCE_ACTION_KEYS.save);
        }
    };

    const testSource = async (source: DataSourceModel) => {
        setTestingSourceID(source.ID);
        SystemActions.PublicActions.beginAction(DATA_SOURCE_ACTION_KEYS.testConnection);
        try {
            const result = await dataSourcesApi.testConnection({
                FacilityID: facility.ID,
                DataConnectionID: source.DataConnectionID,
            });
            SystemActions.PublicActions.displayInfoMessage({
                message: result.Message || (result.Successful
                    ? InovxLoader.t('facility:dataSources.connectionSuccessful')
                    : InovxLoader.t('facility:dataSources.connectionFailed')),
            });
            await load();
        } catch (error) {
            displayApiError(error);
        } finally {
            setTestingSourceID(null);
            SystemActions.PublicActions.endAction(DATA_SOURCE_ACTION_KEYS.testConnection);
        }
    };

    const deleteSource = (source: DataSourceModel) => {
        SystemActions.PublicActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:dataSources.confirmDelete', { name: source.Name }),
            confirmCallback: async () => {
                SystemActions.PublicActions.beginAction(DATA_SOURCE_ACTION_KEYS.delete);
                try {
                    await dataSourcesApi.deleteSource({ FacilityID: facility.ID, ID: source.ID, VersionToken: source.VersionToken });
                    await load();
                } catch (error) {
                    displayApiError(error);
                } finally {
                    SystemActions.PublicActions.endAction(DATA_SOURCE_ACTION_KEYS.delete);
                }
            },
        });
    };

    return (
        <div className="data-sources-page">
            <InovxControls.StaticHeaderForm title={InovxLoader.t('facility:dataSources.title')}>
                <div className="data-sources-content">
                    <div className="data-sources-toolbar">
                        <button type="button" className="e-btn e-primary" onClick={() => setWizardSource(null)}>
                            {InovxLoader.t('facility:dataSources.add')}
                        </button>
                    </div>
                    {loading ? (
                        <div className="data-sources-loading">{InovxLoader.t('facility:dataSources.loading')}</div>
                    ) : sources.length > 0 ? (
                        <DataSourceGrid
                            sources={sources}
                            connectionTypes={connectionTypes}
                            testingSourceID={testingSourceID}
                            onTest={testSource}
                            onEdit={setWizardSource}
                            onDelete={deleteSource}
                        />
                    ) : (
                        <div className="data-sources-empty-state">
                            <div>{InovxLoader.t('facility:dataSources.empty')}</div>
                            <button type="button" className="e-btn e-primary" onClick={() => setWizardSource(null)}>
                                {InovxLoader.t('facility:dataSources.add')}
                            </button>
                        </div>
                    )}
                </div>
            </InovxControls.StaticHeaderForm>
            {wizardSource !== undefined && (
                <DataSourceWizard
                    facilityID={facility.ID}
                    connectionTypes={connectionTypes}
                    source={wizardSource}
                    saving={saving}
                    onSave={saveSource}
                    onCancel={() => setWizardSource(undefined)}
                />
            )}
        </div>
    );
};

export default DataSourcesPage;
